import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { http } from "@/lib/api";
import { Building2, Inbox, Calendar, Mail } from "lucide-react";
import { Link } from "react-router-dom";

const Card = ({ label, value, hint, icon: Icon, testId }) => (
  <div className="border border-[var(--mel-border)] rounded-md p-6 bg-white" data-testid={testId}>
    <div className="flex items-center justify-between mb-4">
      <div className="eyebrow">{label}</div>
      <Icon size={18} className="text-[var(--mel-primary)]" />
    </div>
    <div className="font-serif text-4xl">{value}</div>
    {hint && <div className="text-xs text-[var(--mel-muted)] mt-2">{hint}</div>}
  </div>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentBookings, setRecent] = useState([]);
  const [recentMsgs, setMsgs] = useState([]);

  useEffect(() => {
    http.get("/stats").then((r) => setStats(r.data));
    http.get("/bookings").then((r) => setRecent(r.data.slice(0, 5)));
    http.get("/messages").then((r) => setMsgs(r.data.slice(0, 5)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- runs once on mount

  return (
    <AdminLayout>
      <div className="p-10">
        <div className="eyebrow mb-2">Panoramica</div>
        <h1 className="font-serif text-4xl mb-10">Dashboard</h1>

        <div className="grid md:grid-cols-4 gap-6" data-testid="dashboard-stats">
          <Card label="Proprietà" value={stats?.properties_total ?? '—'} hint={`${stats?.properties_active ?? 0} attive`} icon={Building2} testId="stat-properties" />
          <Card label="Richieste in attesa" value={stats?.bookings_pending ?? '—'} icon={Inbox} testId="stat-pending" />
          <Card label="Prenotazioni confermate" value={stats?.bookings_confirmed ?? '—'} icon={Calendar} testId="stat-confirmed" />
          <Card label="Messaggi non letti" value={stats?.messages_unread ?? '—'} icon={Mail} testId="stat-messages" />
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-10">
          <div className="border border-[var(--mel-border)] rounded-md p-6 bg-white">
            <div className="flex items-center justify-between mb-4">
              <div className="font-serif text-xl">Ultime richieste</div>
              <Link to="/admin/prenotazioni" className="text-xs text-[var(--mel-primary)]" data-testid="see-all-bookings">Vedi tutte</Link>
            </div>
            {recentBookings.length === 0 && <div className="text-sm text-[var(--mel-muted)]">Nessuna richiesta.</div>}
            <div className="space-y-3">
              {recentBookings.map((b) => (
                <div key={b.booking_id} className="flex items-center justify-between py-2 border-b border-[var(--mel-border)] last:border-none">
                  <div>
                    <div className="text-sm font-medium">{b.guest_name}</div>
                    <div className="text-xs text-[var(--mel-muted)]">{b.property_name} · {b.check_in} → {b.check_out}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${b.status === 'confirmed' ? 'bg-[var(--mel-accent)] text-white' : b.status === 'pending' ? 'bg-[var(--mel-primary)] text-white' : 'bg-[var(--mel-surface)]'}`}>{b.status}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="border border-[var(--mel-border)] rounded-md p-6 bg-white">
            <div className="flex items-center justify-between mb-4">
              <div className="font-serif text-xl">Ultimi messaggi</div>
              <Link to="/admin/messaggi" className="text-xs text-[var(--mel-primary)]" data-testid="see-all-messages">Vedi tutti</Link>
            </div>
            {recentMsgs.length === 0 && <div className="text-sm text-[var(--mel-muted)]">Nessun messaggio.</div>}
            <div className="space-y-3">
              {recentMsgs.map((m) => (
                <div key={m.message_id} className="py-2 border-b border-[var(--mel-border)] last:border-none">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">{m.name}</div>
                    <span className="text-[10px] uppercase tracking-widest text-[var(--mel-muted)]">{m.status}</span>
                  </div>
                  <div className="text-xs text-[var(--mel-muted)] line-clamp-1">{m.subject || m.body}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
